import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, Mail, Lock, AlertCircle } from 'lucide-react';
import { quickConnectionTest } from '../utils/diagnoseConnection';
import ForgotPasswordModal from './ForgotPasswordModal';

const Login = ({ theme = 'light' }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login } = useAuth();

  // Verificar conexión al montar (solo en desarrollo)
  useEffect(() => {
    // Solo ejecutar diagnóstico en desarrollo
    if (process.env.NODE_ENV === 'development') {
      const checkConnection = async () => {
        console.log('=== Verificando conexión con el backend ===');
        const result = await quickConnectionTest();
        setConnectionStatus(result);
        if (!result.success) {
          console.error('❌ Problema de conexión detectado:', result.message);
          console.error('Sugerencia:', result.suggestion);
          console.log('');
          console.log('Para diagnosticar, ejecuta en la consola:');
          console.log('import { diagnoseConnection } from "./utils/diagnoseConnection";');
          console.log('diagnoseConnection();');
        } else {
          console.log('✅ Conexión con el backend OK');
        }
      };
      checkConnection();
    }
  }, []);

  const handleQuickLogin = async () => {
    // Llenar automáticamente las credenciales por defecto
    const defaultEmail = 'admin@vinson.com.ar';
    const defaultPassword = 'admin123';
    
    setEmail(defaultEmail);
    setPassword(defaultPassword);
    setError('');
    setLoading(true);

    try {
      const result = await login(defaultEmail, defaultPassword);
      if (!result.success) {
        setError(result.error || 'Error al iniciar sesión');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error en handleQuickLogin:', err);
      const errorMessage = err?.message || err?.toString() || 'Error al conectar con el servidor';
      
      if (err?.isConnectionError || errorMessage.includes('No se pudo conectar')) {
        setError('No se pudo conectar con el servidor. Verifica que el backend esté corriendo en http://127.0.0.1:8000/api');
      } else {
        setError(errorMessage);
      }
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (!result.success) {
        // Mostrar el error específico que viene del backend o del servicio
        setError(result.error || 'Error al iniciar sesión');
      }
    } catch (err) {
      // Mostrar el mensaje de error real, no un genérico
      console.error('Error en handleSubmit:', err);
      const errorMessage = err?.message || err?.toString() || 'Error al conectar con el servidor';
      
      // Si es un error de conexión, mostrar mensaje más claro
      if (err?.isConnectionError || errorMessage.includes('No se pudo conectar')) {
        setError('No se pudo conectar con el servidor. Verifica que el backend esté corriendo en http://127.0.0.1:8000/api');
      } else {
        // Mostrar el mensaje de error real del backend
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const isDark = theme === 'dark';

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${
      isDark ? 'bg-[#1f1f1f]' : 'bg-gray-100'
    }`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-xl shadow-lg border p-8 w-full max-w-md ${
          isDark ? 'bg-[#202124] text-[#e8eaed] border-[#3c4043]' : 'bg-white border-gray-200'
        }`}
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-xl mb-4">
            <LogIn className="w-8 h-8 text-white" />
          </div>
          <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-[#e8eaed]' : 'text-gray-900'}`}>Orilat</h1>
          <p className={isDark ? 'text-[#bdc1c6]' : 'text-gray-600'}>Inicia sesión para continuar</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {connectionStatus && !connectionStatus.success && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className={`border px-4 py-3 rounded-lg flex items-center gap-2 ${
                isDark ? 'bg-[#3c2f00] border-[#5f4b00] text-[#f2c94c]' : 'bg-yellow-50 border-yellow-200 text-yellow-700'
              }`}
            >
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{connectionStatus.message}</span>
            </motion.div>
          )}
          
          {error && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className={`border px-4 py-3 rounded-lg flex items-center gap-2 ${
                isDark ? 'bg-[#3a1515] border-[#5c1f1f] text-[#f28b82]' : 'bg-red-50 border-red-200 text-red-700'
              }`}
            >
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </motion.div>
          )}

          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-[#e8eaed]' : 'text-gray-700'}`}>
              Email
            </label>
            <div className="relative">
              <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-[#9aa0a6]' : 'text-gray-400'}`} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none ${
                  isDark ? 'bg-[#1f1f1f] text-[#e8eaed] border-[#3c4043] placeholder-[#9aa0a6]' : 'border-gray-300'
                }`}
                placeholder="admin@vinson.com.ar"
                required
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`block text-sm font-medium ${isDark ? 'text-[#e8eaed]' : 'text-gray-700'}`}>
                Contraseña
              </label>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className={`text-sm transition-colors ${isDark ? 'text-[#8ab4f8] hover:text-[#aecbfa]' : 'text-blue-600 hover:text-blue-800'}`}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
            <div className="relative">
              <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-[#9aa0a6]' : 'text-gray-400'}`} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none ${
                  isDark ? 'bg-[#1f1f1f] text-[#e8eaed] border-[#3c4043] placeholder-[#9aa0a6]' : 'border-gray-300'
                }`}
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </motion.button>
        </form>

        
      </motion.div>
      
      {/* Footer */}
      <div className="fixed bottom-0 left-0 p-4 z-10">
        <p className={`text-xs ${isDark ? 'text-[#bdc1c6]' : 'text-gray-500'}`}>
          Powered by Orilat | © Orilat 2026
        </p>
      </div>

      {/* Modal de recuperación de contraseña */}
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </div>
  );
};

export default Login;

